import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Shield, CheckCircle2, Clock, XCircle, MinusCircle, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth, API } from "../App";
import { extractError } from "../lib/errors";
import { toast } from "sonner";

const STATUS_META = {
  compliant:       { label: "Conforme",        icon: CheckCircle2, color: "text-emerald-500", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  partial:         { label: "Parcial",          icon: Clock,        color: "text-amber-500",   badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  non_compliant:   { label: "No Conforme",      icon: XCircle,      color: "text-red-500",     badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  not_applicable:  { label: "No Aplica",        icon: MinusCircle,  color: "text-slate-400",   badge: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const PILLARS_ORDER = [
  "cumplimiento_aduanero",
  "solidez_financiera",
  "socios_comerciales",
  "seguridad_fisica",
  "seguridad_ti",
];

const PILLAR_LABELS = {
  cumplimiento_aduanero: "Gestión de Cumplimiento Aduanero",
  solidez_financiera: "Solidez Financiera",
  socios_comerciales: "Seguridad de Socios Comerciales",
  seguridad_fisica: "Seguridad Física y de Instalaciones",
  seguridad_ti: "Seguridad de TI y Gestión de Personal",
};

const ControlRow = ({ control, response, onSave, onSuggest }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(response?.status || "");
  const [notes, setNotes] = useState(response?.notes || "");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const handleSave = async () => {
    if (!status) return;
    setSaving(true);
    await onSave(control.id, { status, notes });
    setSaving(false);
  };

  const handleSuggest = async () => {
    setAiLoading(true);
    const text = await onSuggest(control);
    if (text) setNotes((prev) => (prev ? `${prev}\n\n${text}` : text));
    setAiLoading(false);
  };

  const meta = STATUS_META[status];

  return (
    <div className="border border-border rounded-lg">
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{control.title}</span>
            {meta && (
              <Badge variant="outline" className={`text-[10px] ${meta.badge} flex-shrink-0`}>
                <meta.icon className="w-2.5 h-2.5 mr-1" />
                {meta.label}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{control.description}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-muted-foreground">Peso: {control.weight}</span>
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">{control.description}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Estado de cumplimiento</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Seleccionar estado..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([val, m]) => (
                    <SelectItem key={val} value={val}>
                      <div className="flex items-center gap-2">
                        <m.icon className={`w-3.5 h-3.5 ${m.color}`} />
                        {m.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Notas / Evidencia</label>
            <Textarea
              placeholder="Descripción de evidencia o notas de auditoría..."
              className="text-sm h-20 resize-none"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={!status || saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleSuggest} disabled={aiLoading}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {aiLoading ? "Generando..." : "Sugerir con AI"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ZonaFrancaOEA = () => {
  const { token } = useAuth();
  const [controls, setControls] = useState([]);
  const [assessment, setAssessment] = useState({});
  const [loading, setLoading] = useState(true);
  const [openPillars, setOpenPillars] = useState({ cumplimiento_aduanero: true });

  const load = useCallback(async () => {
    try {
      const [ctrlRes, assRes] = await Promise.all([
        axios.get(`${API}/zona-franca/controls/oea`),
        axios.get(`${API}/zona-franca/assessment/oea`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setControls(ctrlRes.data.controls || []);
      setAssessment(assRes.data.responses || {});
    } catch {
      toast.error("No se pudo cargar la evaluación OEA");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (controlId, body) => {
    try {
      const res = await axios.put(`${API}/zona-franca/assessment/oea/${controlId}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssessment((prev) => ({
        ...prev,
        [controlId]: { ...body },
      }));
      toast.success(`Control actualizado — ${res.data.completion_pct}% completado`);
    } catch {
      toast.error("Error al guardar el control");
    }
  };

  const handleSuggest = async (control) => {
    try {
      const res = await axios.post(
        `${API}/zona-franca/ai/suggest`,
        {
          framework_code: "oea",
          control_id: control.id,
          control_title: control.title,
          control_description: control.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Sugerencia generada");
      return res.data.suggestion;
    } catch (err) {
      toast.error(extractError(err, "No se pudo generar sugerencia"));
      return "";
    }
  };

  const byPillar = PILLARS_ORDER.reduce((acc, pillar) => {
    acc[pillar] = controls.filter((c) => c.pillar === pillar);
    return acc;
  }, {});

  const answered = Object.keys(assessment).length;
  const total = controls.length;
  const compliant = Object.values(assessment).filter((r) => r.status === "compliant").length;
  const completionPct = total ? Math.round((answered / total) * 100) : 0;
  const compliancePct = total ? Math.round((compliant / total) * 100) : 0;

  const togglePillar = (key) =>
    setOpenPillars((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#f59e0b20", border: "1px solid #f59e0b40" }}>
            <Shield className="w-5 h-5" style={{ color: "#f59e0b" }} />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold">OEA — Operador Económico Autorizado</h1>
            <p className="text-sm text-muted-foreground">Evaluación de los 20 controles en 5 pilares — Marco SAFE de la OMA</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">DGA / Aduana RD</Badge>
      </div>

      {/* Progress */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: "Evaluación completada", value: `${completionPct}%`, sub: `${answered} / ${total} controles evaluados` },
          { label: "Conformidad", value: `${compliancePct}%`, sub: `${compliant} controles conformes` },
          { label: "Controles pendientes", value: total - answered, sub: "por evaluar" },
        ].map((kpi) => (
          <Card key={kpi.label}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold" style={{ color: "#f59e0b" }}>{loading ? "—" : kpi.value}</p>
              <p className="text-sm font-medium">{kpi.label}</p>
              <p className="text-xs text-muted-foreground">{kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between text-sm mb-1">
            <span>Progreso de evaluación OEA</span>
            <span className="font-medium">{completionPct}%</span>
          </div>
          <Progress value={completionPct} className="h-2" />
        </CardContent>
      </Card>

      {/* Pillars */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {PILLARS_ORDER.map((pillar) => {
            const items = byPillar[pillar] || [];
            const pillarAnswered = items.filter((c) => assessment[c.id]?.status).length;
            const pillarCompliant = items.filter((c) => assessment[c.id]?.status === "compliant").length;
            const isOpen = openPillars[pillar] ?? false;

            return (
              <Card key={pillar}>
                <CardHeader className="pb-0">
                  <button
                    className="w-full flex items-center justify-between text-left"
                    onClick={() => togglePillar(pillar)}
                  >
                    <div className="flex items-center gap-3">
                      <Shield className="w-4 h-4" style={{ color: "#f59e0b" }} />
                      <div>
                        <CardTitle className="text-sm">{PILLAR_LABELS[pillar]}</CardTitle>
                        <CardDescription className="text-xs">
                          {pillarCompliant} conformes · {pillarAnswered}/{items.length} evaluados
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress
                        value={items.length ? Math.round((pillarAnswered / items.length) * 100) : 0}
                        className="h-1.5 w-24"
                      />
                      {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </button>
                </CardHeader>
                {isOpen && (
                  <CardContent className="pt-3 space-y-2">
                    {items.map((ctrl) => (
                      <ControlRow
                        key={ctrl.id}
                        control={ctrl}
                        response={assessment[ctrl.id]}
                        onSave={handleSave}
                        onSuggest={handleSuggest}
                      />
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZonaFrancaOEA;
